import { useEffect, useState } from "react";
import api from "../lib/api";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [tx, setTx] = useState(null);
  const [tries, setTries] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }

    let cancelled = false;
    let count = 0;
    const tick = async () => {
      if (cancelled) return;
      count += 1;
      setTries(count);
      try {
        const { data } = await api.get(`/payments/checkout/status/${sessionId}`);
        setTx(data);
        if (data.payment_status === "paid") { setStatus("paid"); return; }
        if (data.status === "expired") { setStatus("expired"); return; }
        if (count >= 8) { setStatus("timeout"); return; }
        setTimeout(tick, 2000);
      } catch {
        setTimeout(tick, 2000);
      }
    };
    tick();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="container-x py-20 max-w-2xl mx-auto" data-testid="checkout-success-page">
      <div className="rounded-2xl border border-border bg-card p-10 text-center">
        {status === "checking" && (
          <>
            <div className="mx-auto h-12 w-12 animate-spin rounded-full border-2 border-foreground/20 border-t-foreground" />
            <h1 className="mt-6 font-display text-3xl">Confirming your payment…</h1>
            <p className="mt-2 text-muted-foreground text-sm">Hang tight — verifying with Stripe (try {tries}).</p>
          </>
        )}
        {status === "paid" && (
          <>
            <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 grid place-items-center">
              <CheckCircle2 className="h-8 w-8 text-emerald-600" />
            </div>
            <h1 className="mt-6 font-display text-3xl tracking-tight">You're in. Welcome to BeforeExam Pro.</h1>
            <p className="mt-2 text-muted-foreground">
              {tx?.type === "course" ? "Your course is unlocked. Time to start your streak." : "Your subscription is active. Browse and practice unlimited."}
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/dashboard"><button className="btn-primary">Go to dashboard <ArrowRight className="h-4 w-4" /></button></Link>
              <Link to="/marketplace"><button className="btn-ghost">Browse more <Sparkles className="h-4 w-4" /></button></Link>
            </div>
          </>
        )}
        {status === "timeout" && (
          <>
            <h1 className="font-display text-3xl">Still processing…</h1>
            <p className="mt-2 text-muted-foreground text-sm">Your payment may take a moment to confirm. Please refresh in a few seconds.</p>
            <button onClick={() => window.location.reload()} className="mt-6 btn-primary">Refresh</button>
          </>
        )}
        {status === "expired" && (
          <>
            <h1 className="font-display text-3xl">Session expired</h1>
            <p className="mt-2 text-muted-foreground text-sm">No worries — try again from the marketplace.</p>
            <button onClick={() => navigate("/marketplace")} className="mt-6 btn-primary">Back to marketplace</button>
          </>
        )}
        {status === "error" && (
          <>
            <h1 className="font-display text-3xl">Missing session</h1>
            <p className="mt-2 text-muted-foreground text-sm">No checkout session found.</p>
            <button onClick={() => navigate("/")} className="mt-6 btn-primary">Home</button>
          </>
        )}
      </div>
    </div>
  );
}
